import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";
import {
  Scale, Sparkles, MapPin, ArrowUpRight, Clock, Building2, Video,
  BadgeCheck, Gavel, ChevronRight,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   HomeShowcase — carruseles + delegaciones en vivo (public)
   ═══════════════════════════════════════════════════════════════ */

const CLP = (n) => n ? `$${Number(n).toLocaleString("es-CL")}` : "—";

const timeAgo = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "recién";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} día${d > 1 ? "s" : ""}`;
};

export default function HomeShowcase() {
  const [featured, setFeatured] = useState({ fundadores: [], mediadores: [] });
  const [delegations, setDelegations] = useState([]);

  useEffect(() => {
    api.get("/home/featured").then(({ data }) => setFeatured(data)).catch(() => {});
    api.get("/home/live-delegations?limit=6").then(({ data }) => setDelegations(data.items || [])).catch(() => {});
  }, []);

  const anyFeatured = featured.fundadores.length > 0 || featured.mediadores.length > 0;

  return (
    <section className="container-app py-20" data-testid="home-showcase">
      {/* Fundadores */}
      {featured.fundadores.length > 0 && (
        <div className="mb-16" data-testid="home-fundadores">
          <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
            <div>
              <div className="label-eyebrow text-amber-300 inline-flex items-center gap-2"><Sparkles size={12}/> Miembros Fundadores</div>
              <h2 className="serif text-4xl md:text-5xl mt-3 leading-[1]">Abogados <span className="brand-gradient-text">fundadores</span></h2>
              <p className="mt-2 text-sm text-muted-foreground max-w-xl">Los primeros 50 abogados verificados de Colega Link. Precio congelado de por vida.</p>
            </div>
            <Link to="/planes" className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="home-cta-planes">
              Ver planes <ChevronRight size={12}/>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.fundadores.slice(0, 8).map((p) => (
              <div key={p.id} className="panel p-5 flex flex-col items-start hover:border-amber-400/40 transition-colors" data-testid={`home-fundador-${p.id}`}>
                <div className="flex items-center gap-3 w-full">
                  <Avatar name={p.name} src={p.avatar_url} size={48} testid={`fund-avatar-${p.id}`}/>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><BadgeCheck size={10} className="text-amber-300"/> Fundador #{p.founder_number}</div>
                  </div>
                </div>
                {p.specialties?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {p.specialties.slice(0, 3).map((s) => <span key={s} className="pill text-[10px]">{s}</span>)}
                  </div>
                )}
                {(p.region || p.comuna) && (
                  <div className="mt-3 text-[11px] text-muted-foreground inline-flex items-center gap-1"><MapPin size={10}/> {p.comuna || p.region}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mediadores destacados */}
      {featured.mediadores.length > 0 && (
        <div className="mb-16" data-testid="home-mediadores">
          <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
            <div>
              <div className="label-eyebrow text-emerald-300 inline-flex items-center gap-2"><Scale size={12}/> Directorio verificado</div>
              <h2 className="serif text-4xl md:text-5xl mt-3 leading-[1]">Mediadores <span className="brand-gradient-text">destacados</span></h2>
              <p className="mt-2 text-sm text-muted-foreground max-w-xl">Mediadores familiares privados con credenciales verificadas y tarifario público.</p>
            </div>
            <Link to="/mediacion-familiar" className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="home-cta-mediadores">
              Ver directorio <ChevronRight size={12}/>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.mediadores.slice(0, 6).map((m) => (
              <Link
                key={m.id}
                to={`/app/mediadores/${m.id}`}
                className="panel p-5 flex flex-col items-start hover:border-emerald-400/40 transition-colors group"
                data-testid={`home-mediador-${m.id}`}
              >
                <div className="flex items-center gap-3 w-full">
                  <Avatar name={m.name} src={m.avatar_url} size={52} testid={`med-avatar-${m.id}`}/>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate group-hover:text-emerald-200 transition-colors">{m.name}</div>
                    <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><BadgeCheck size={10} className="text-emerald-300"/> Verificado</div>
                  </div>
                </div>
                {m.specialties?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {m.specialties.slice(0, 3).map((s) => <span key={s} className="pill text-[10px] badge-cyan">{s}</span>)}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
                  {m.attends_presencial && <span className="pill text-[10px] border-cyan-400/40 bg-cyan-400/10 text-cyan-200 inline-flex items-center gap-1"><Building2 size={9}/> Presencial</span>}
                  {m.attends_remote && <span className="pill text-[10px] border-cyan-400/40 bg-cyan-400/10 text-cyan-200 inline-flex items-center gap-1"><Video size={9}/> Telemática</span>}
                </div>
                {m.mediator_tariff?.valor_base_frustrada_clp > 0 && (
                  <div className="mt-3 text-xs text-muted-foreground">Desde <strong className="text-foreground">{CLP(m.mediator_tariff.valor_base_frustrada_clp)}</strong></div>
                )}
                {(m.region || m.comuna) && (
                  <div className="mt-2 text-[11px] text-muted-foreground inline-flex items-center gap-1"><MapPin size={10}/> {m.comuna || m.region}</div>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Delegaciones en vivo */}
      {delegations.length > 0 && (
        <div data-testid="home-live-delegations">
          <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
            <div>
              <div className="label-eyebrow text-cyan-300 inline-flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"/>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400"/>
                </span>
                En vivo
              </div>
              <h2 className="serif text-4xl md:text-5xl mt-3 leading-[1]">Delegaciones <span className="brand-gradient-text">publicándose ahora</span></h2>
              <p className="mt-2 text-sm text-muted-foreground max-w-xl">Últimas gestiones y audiencias solicitadas por colegas verificados. Datos sensibles ocultos.</p>
            </div>
            <Link to="/registro/abogado" className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="home-cta-lawyer">
              Crear cuenta de Abogado <ArrowUpRight size={12}/>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {delegations.map((d) => (
              <div key={d.id} className="panel p-4" data-testid={`home-live-${d.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="pill text-[10px] badge-cyan"><Gavel size={9} className="mr-0.5"/> {d.specialty || "Delegación"}</span>
                  {d.hearing_type && <span className="pill text-[10px]">{d.hearing_type}</span>}
                  <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1 ml-auto"><Clock size={9}/> {timeAgo(d.created_at)}</span>
                </div>
                <h3 className="text-sm font-medium mt-2 line-clamp-2">{d.title || "Delegación abierta"}</h3>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground inline-flex items-center gap-1"><MapPin size={10}/> {d.region || "Chile"}</span>
                  {d.budget_range_clp > 0 && <span className="text-foreground/90 font-medium">{CLP(d.budget_range_clp)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!anyFeatured && delegations.length === 0 && null}
    </section>
  );
}
